import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

/** D-124: inverted primary fill (black↔white). Shared with Segmented selected. */
export const invertedFillClass =
  "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white";

/** D-124: glass outline secondary. Shared with Segmented unselected.
 *  Light = frosted white (cosmic mesh is dark at the top). Dark stays hollow. */
export const glassOutlineClass =
  "border border-white/70 bg-white/70 text-slate-800 hover:bg-white/90 dark:border-white/15 dark:bg-transparent dark:text-slate-100 dark:hover:bg-white/10";

/** D-126: native <select> closed trigger. Same frost as secondary; slate focus, not appleBlue. */
export const glassSelectClass =
  "h-8 px-3 rounded-xl border border-white/70 bg-white/70 text-xs font-semibold text-slate-800 appearance-none outline-none focus:ring-2 focus:ring-slate-400/40 dark:border-white/15 dark:bg-transparent dark:text-slate-100 dark:focus:ring-slate-500/40";

/** D-126: ghost icon trash/delete — red ink, no red fill. */
export const ghostDangerClass =
  "text-red-500 hover:bg-red-50 hover:text-red-600 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: invertedFillClass,
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: glassOutlineClass,
        secondary: glassOutlineClass,
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(function Button({ className, variant, size, asChild = false, ...props }, ref) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
